import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["alert", "aria-label", "aria-labelledby", "badgeContent", "badgeStyle", "className", "dot", "fwdRef", "icon", "iconProps", "max", "status", "style"];
import React, { forwardRef } from 'react';
import { IconAlert, IconError, IconWarning } from '@op/opds.opux-icons/indicator';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
function getAlertIcon(alert) {
  if (alert) {
    if (alert === 'critical') {
      return /*#__PURE__*/React.createElement(IconError, {
        color: "negative"
      });
    } else if (alert === 'warning') {
      return /*#__PURE__*/React.createElement(IconWarning, {
        color: "warning"
      });
    } else if (alert === 'notification') {
      return /*#__PURE__*/React.createElement(IconAlert, {
        color: "notification"
      });
    }
  }
  return null;
}

/**
 * _A small information conveyer to highlight and draw attention to UI elements._
 *
 * ***
 *
 * Badges can display numbers or strings with the `badgeContent`
 * or an `icon`. Numerical values can be set to have `max` to
 * display a plus sign to indicate the value is greater. The
 * `alert` badges should only be used with numerical values.
 * The `status` badges have fixed presentation.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<div>` element.
 * The `badgeStyle` affects the `<span>` element.
 */
export const Badge = _ref => {
  let {
      alert,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      badgeContent,
      badgeStyle,
      className,
      dot,
      fwdRef,
      icon: Icon,
      iconProps,
      max,
      status,
      style
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  let content = badgeContent;
  let AlertIcon = null;
  const role = ariaLabel || ariaLabelledBy ? 'img' : 'presentation';
  if (badgeContent && typeof badgeContent === 'number' && max && badgeContent > max) {
    content = max + "+";
  }
  if (Icon) {
    content = /*#__PURE__*/React.createElement(Icon, _extends({
      size: "small"
    }, iconProps));
  }
  AlertIcon = getAlertIcon(alert);
  return /*#__PURE__*/React.createElement("div", _extends({
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    className: buildVersionedClassNames('ds-badge', className, {
      'ds-badge--alert': alert && !status,
      'ds-badge--alert-critical': alert === 'critical' && !status,
      'ds-badge--alert-warning': alert === 'warning' && !status,
      'ds-badge--dot': dot && !Icon && !status,
      ["ds-badge--status-dot-" + status]: status && !alert && dot,
      ["ds-badge--status-" + status]: status && !alert && !dot,
      'ds-badge--large': badgeContent && content && (typeof content === 'string' || typeof content === 'number') && content.toString().length > 1 && !status && !dot && !Icon
    }),
    ref: fwdRef,
    role: role,
    style: style
  }, props), AlertIcon, /*#__PURE__*/React.createElement("span", {
    className: buildVersionedClass('ds-badge__content'),
    style: badgeStyle
  }, !dot && content));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Badge, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'Badge';
export default Fwd;